import { Injectable, Renderer2, RendererFactory2, signal, inject } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { StorageService } from './storage.service';
import { AnalyticsService } from './analytics.service';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private storage = inject(StorageService);
  private analyticsService = inject(AnalyticsService);

  private readonly THEME_KEY = 'myabvv-theme';
  private renderer: Renderer2;

  private readonly _theme = signal<Theme>('system');
  private readonly _isDarkMode = signal(false);

  readonly theme = this._theme.asReadonly();
  readonly darkMode = this._isDarkMode.asReadonly();

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(): void {
    const savedTheme = this.storage.get(this.THEME_KEY) as Theme;
    const theme = savedTheme || 'system';
    this.applyTheme(theme);
    this.watchSystemPreference();
  }

  getTheme(): Theme {
    return this._theme();
  }

  isDarkMode(): boolean {
    return this._isDarkMode();
  }

  setTheme(theme: Theme): void {
    const previousTheme = this._theme();
    this.storage.set(this.THEME_KEY, theme);
    this.applyTheme(theme);

    if (previousTheme !== theme) {
      this.analyticsService.trackEvent('theme_change', {
        from: previousTheme,
        to: theme
      });
    }
  }

  toggleDarkMode(): void {
    const currentEffectiveDark = this._isDarkMode();
    this.setTheme(currentEffectiveDark ? 'light' : 'dark');
  }

  private applyTheme(theme: Theme): void {
    this._theme.set(theme);

    let isDark: boolean;
    if (theme === 'system') {
      isDark = this.getSystemPreference();
    } else {
      isDark = theme === 'dark';
    }

    this.setDarkMode(isDark);
  }

  private setDarkMode(isDark: boolean): void {
    this._isDarkMode.set(isDark);

    if (isDark) {
      this.renderer.addClass(document.documentElement, 'dark');
    } else {
      this.renderer.removeClass(document.documentElement, 'dark');
    }

    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({
        style: isDark ? Style.Dark : Style.Light
      }).catch(() => {});
    }
  }

  private getSystemPreference(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private watchSystemPreference(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      if (this._theme() === 'system') {
        this.setDarkMode(e.matches);
      }
    });
  }
}
